import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import {
  getQuotations,
  deleteQuotation,
  getSettings,
  convertQuotationToPI,
  convertPIToSOA
} from "../services/firestore";

import { generateQuotationPDF } from "../services/pdfGenerator";

import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../components/ui/alert-dialog";

import {
  Plus,
  Search,
  FileText,
  Building2,
  MoreVertical,
  Pencil,
  Trash2,
  Download,
  ArrowRightLeft,
  Receipt
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";

import { toast } from "sonner";

export const QuotationsPage = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();

  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);

  const [settings, setSettings] = useState({});

  const [dateFilter, setDateFilter] = useState("all");
  const [customDate, setCustomDate] = useState("");

  /* =============================
     LOAD DATA
  ==============================*/

  useEffect(() => {
    if (userData) fetchData();
  }, [userData]);

  useEffect(() => {
  filterQuotations();
  }, [searchQuery, statusFilter, dateFilter, customDate, quotations]);

  const fetchData = async () => {
    setLoading(true);

    try {
      const [quotationData, settingsData] = await Promise.all([
        getQuotations(),
        getSettings()
      ]);

      setQuotations(quotationData);
      setFilteredQuotations(quotationData);
      setSettings(settingsData);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  };

  /* =============================
     SEARCH FILTER
  ==============================*/

  const filterQuotations = () => {

  let result = [...quotations];

  // SEARCH
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();

    result = result.filter(doc =>
      doc.quotation_no?.toLowerCase().includes(q) ||
      doc.party_snapshot?.name?.toLowerCase().includes(q)
    );
  }

  // STATUS
  if (statusFilter !== "all") {
    result = result.filter(doc => (doc.status || "draft") === statusFilter);
  }

  // DATE FILTER
  if (dateFilter !== "all") {

    const now = new Date();

    result = result.filter(doc => {

      const dRaw = doc.created_at?.toDate?.() || doc.created_at;
      if (!dRaw) return false;

      const d = new Date(dRaw);
      if (isNaN(d)) return false;

      if (dateFilter === "last7") {
        const past = new Date();
        past.setDate(now.getDate() - 7);
        return d >= past;
      }

      if (dateFilter === "last30") {
        const past = new Date();
        past.setDate(now.getDate() - 30);
        return d >= past;
      }

      if (dateFilter === "custom") {
        if (!customDate) return false;

        const selected = new Date(customDate);
        selected.setHours(0, 0, 0, 0);

        const dDate = new Date(d);
        dDate.setHours(0, 0, 0, 0);

        return dDate >= selected;
      }

      return true;

    });
  }

  setFilteredQuotations(result);
};

  /* =============================
     DELETE
  ==============================*/

  const handleDelete = async () => {
    if (!quotationToDelete) return;

    try {
      await deleteQuotation(quotationToDelete.id);
      toast.success("Quotation deleted");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete quotation");
    } finally {
      setDeleteDialogOpen(false);
      setQuotationToDelete(null);
    }
  };

  /* =============================
     PDF DOWNLOAD
  ==============================*/

  const handleDownloadPDF = (quotation) => {
    try {
      generateQuotationPDF(
        quotation,
        settings,
        userData?.name || "user"
      );
      toast.success("PDF downloaded");
    } catch (error) {
      console.error(error);
      toast.error("PDF generation failed");
    }
  };

  /* =============================
     CONVERSIONS
  ==============================*/

  const handleConvertToPI = async (quotation) => {
    try {
      const result = await convertQuotationToPI(quotation);
      toast.success(`PI ${result.pi_no} created`);
      navigate(`/proforma-invoices/${result.id}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to convert quotation");
    }
  };

  const handleConvertToSOA = async (quotation) => {
    try {
      const result = await convertPIToSOA(quotation);
      toast.success(`SOA ${result.soa_no} created`);
      navigate(`/soa/${result.id}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to convert quotation");
    }
  };

  /* =============================
     HELPERS
  ==============================*/

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const finalStatus = status || "draft";

    const map = {
      draft: { label: "Draft", className: "badge-draft" },
      submitted: { label: "Submitted", className: "badge-submitted" },
      won: { label: "Won", className: "badge-approved" },
      lost: { label: "Lost", className: "badge-lost" },
      in_process: { label: "In Process", className: "badge-inprocess" }
    };

    const cfg = map[finalStatus] || map.draft;

    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  /* =============================
     CARD
  ==============================*/

  const QuotationCard = ({ quotation }) => (
    <Card className="data-card">
      <CardContent className="p-4">
        <div className="flex justify-between">
          <div className="flex gap-3 flex-1">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>

            <div className="flex-1">
              <div className="flex gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold">
                  {quotation.quotation_no}
                </span>

                {getStatusBadge(quotation.status)}
              </div>

              <p className="text-sm text-slate-500 flex gap-1 mt-1">
                <Building2 size={14} />
                {quotation.party_snapshot?.name || "No party"}
              </p>

              <p className="text-xs text-slate-400 mt-1">
                {formatDate(quotation.created_at)}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => navigate(`/quotations/${quotation.id}`)}
              >
                <FileText size={14} className="mr-2" />
                View
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => navigate(`/quotations/${quotation.id}/edit`)}
              >
                <Pencil size={14} className="mr-2" />
                Edit
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleDownloadPDF(quotation)}
              >
                <Download size={14} className="mr-2" />
                Download PDF
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={() => handleConvertToPI(quotation)}
              >
                <Receipt size={14} className="mr-2" />
                Convert to PI
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleConvertToSOA(quotation)}
              >
                <ArrowRightLeft size={14} className="mr-2" />
                Convert to SOA
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  setQuotationToDelete(quotation);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 size={14} className="mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex justify-between mt-3 pt-3 border-t">
          <span className="text-xs text-slate-500">
            {quotation.items?.length || 0} item(s)
          </span>

          <span className="text-lg font-semibold">
            {formatCurrency(quotation.totals?.grand_total)}
          </span>
        </div>
      </CardContent>
    </Card>
  );

  /* =============================
     LOADING
  ==============================*/

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-full" />
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  /* =============================
     PAGE
  ==============================*/

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Quotations</h1>
          <p className="text-sm text-slate-500">
            {filteredQuotations.length} quotations
          </p>
        </div>

        <Button
          className="solar-gradient"
          onClick={() => navigate("/quotations/new")}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Quotation
        </Button>
      </div>

<div className="flex gap-3 flex-wrap">

  {/* SEARCH */}
  <div className="relative flex-1">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />

    <Input
      placeholder="Search..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      className="pl-10 h-12"
    />
  </div>

  {/* STATUS FILTER */}
  <select
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    className="border rounded px-3 h-12"
  >
    <option value="all">All</option>
    <option value="draft">Draft</option>
    <option value="submitted">Submitted</option>
    <option value="won">Won</option>
    <option value="lost">Lost</option>
    <option value="in_process">In Process</option>
  </select>

  {/*  ADD DATE FILTER HERE */}
  <select
    value={dateFilter}
    onChange={(e) => setDateFilter(e.target.value)}
    className="border rounded px-3 h-12"
  >
    <option value="all">All Time</option>
    <option value="last7">Last 7 Days</option>
    <option value="last30">Last 30 Days</option>
    <option value="custom">Custom Date</option>
  </select>

  {/* 👉 CUSTOM DATE INPUT */}
  {dateFilter === "custom" && (
    <input
      type="date"
      value={customDate}
      onChange={(e) => setCustomDate(e.target.value)}
      className="border rounded px-3 h-12"
    />
  )}

<Button
  variant="outline"
  onClick={() => {
    setSearchQuery("");
    setStatusFilter("all");
    setDateFilter("all");
    setCustomDate("");
  }}
>
  Clear
</Button>

</div>

      {filteredQuotations.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No quotations found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredQuotations.map((q) => (
            <QuotationCard key={q.id} quotation={q} />
          ))}
        </div>
      )}

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete Quotation?
            </AlertDialogTitle>

            <AlertDialogDescription>
              Are you sure you want to delete "
              {quotationToDelete?.quotation_no}" ?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              className="bg-red-500"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default QuotationsPage;